<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview v-bind:title="'Chi tiết yêu cầu bổ sung NS'">
        <template v-slot:toolbar>
          <!-- Sửa: DRAFT hoặc PENDING (người tạo) -->
          <b-button
            v-if="request.canEdit"
            variant="primary"
            size="sm"
            class="mr-2"
            @click="editRequest"
          >
            <i class="fas fa-edit"></i> Sửa
          </b-button>

          <!-- DRAFT actions -->
          <template v-if="request.status === BUDGET_LINE_REQUEST_STATUS.DRAFT">
            <b-button
              variant="success"
              size="sm"
              class="mr-2"
              @click="showSubmitModal"
            >
              <i class="fas fa-paper-plane"></i> Gửi duyệt
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              class="mr-2"
              @click="confirmDelete"
            >
              <i class="fas fa-trash-alt"></i> Xóa
            </b-button>
          </template>

          <!-- PENDING/BROWSING + canApprove actions -->
          <template v-if="(request.status === BUDGET_LINE_REQUEST_STATUS.PENDING || request.status === BUDGET_LINE_REQUEST_STATUS.BROWSING) && request.canApprove">
            <b-button
              variant="success"
              size="sm"
              class="mr-2"
              @click="showApproveModal"
            >
              <i class="fas fa-check"></i> Duyệt
            </b-button>
            <b-button
              variant="danger"
              size="sm"
              class="mr-2"
              @click="showRejectModal"
            >
              <i class="fas fa-times"></i> Không duyệt
            </b-button>
          </template>

          <!-- Hoàn duyệt -->
          <template v-if="request.canUndo">
            <b-button
              variant="warning"
              size="sm"
              class="mr-2"
              @click="showUndoModal"
            >
              <i class="fas fa-undo"></i> Hoàn duyệt
            </b-button>
          </template>

          <b-button variant="secondary" size="sm" class="mr-2" @click="showHistoryModal">
            <i class="flaticon2-time"></i>&nbsp; Lịch sử
          </b-button>
          <b-button variant="secondary" size="sm" @click="goBack">
            <i class="fas fa-arrow-left"></i> Quay lại
          </b-button>
        </template>

        <template v-slot:preview>
          <div class="blr-detail-layout" ref="splitLayout">
            <div class="blr-detail-layout__main" :style="{ width: splitMainWidth }">
              <!-- Header info -->
              <b-row class="mb-4">
                <b-col cols="3">
                  <small class="text-muted">Kỳ</small>
                  <div><strong>{{ formatPeriod(request.period) }}</strong></div>
                </b-col>
                <b-col cols="3">
                  <small class="text-muted">Trạng thái</small>
                  <div>
                    <b-badge :variant="statusVariant(request.status)">
                      {{ statusName(request.status) }}
                    </b-badge>
                  </div>
                </b-col>
                <b-col cols="3">
                  <small class="text-muted">Phòng ban</small>
                  <div>{{ request.departmentName || request.departmentId }}</div>
                </b-col>
                <b-col cols="3">
                  <small class="text-muted">Cửa hàng</small>
                  <div>{{ request.storeName || 'Cấp phòng ban' }}</div>
                </b-col>
              </b-row>

              <b-row class="mb-4">
                <b-col cols="3">
                  <small class="text-muted">Khoản mục</small>
                  <div>{{ request.costItemName || request.costItemId }}</div>
                </b-col>
                <b-col cols="3">
                  <small class="text-muted">Số tiền yêu cầu</small>
                  <div>
                    <strong class="text-primary">{{ convertPrice(request.requestedAmount) }} đ</strong>
                  </div>
                </b-col>
                <b-col cols="3">
                  <small class="text-muted">Người tạo</small>
                  <div>{{ request.createdByName }}</div>
                </b-col>
                <b-col cols="3">
                  <small class="text-muted">Ngày tạo</small>
                  <div>{{ formatDateTime(request.createdAt) }}</div>
                </b-col>
              </b-row>

              <b-row v-if="request.reason" class="mb-4">
                <b-col cols="12" md="8">
                  <small class="text-muted">Lý do yêu cầu</small>
                  <div>{{ request.reason }}</div>
                </b-col>
              </b-row>

              <!-- Link ngân sách nếu đã duyệt -->
              <b-row
                v-if="request.status === BUDGET_LINE_REQUEST_STATUS.APPROVED && request.budgetLineId"
                class="mb-4"
              >
                <b-col cols="12">
                  <b-alert variant="success" show>
                    Yêu cầu đã được duyệt.
                    <router-link
                      :to="{ name: 'detail-budget-line', query: { id: request.budgetLineId } }"
                      class="alert-link ml-2"
                    >
                      Xem ngân sách
                    </router-link>
                  </b-alert>
                </b-col>
              </b-row>

              <!-- Tệp đính kèm -->
              <b-row class="mb-4">
                <b-col cols="12">
                  <small class="text-muted d-block mb-1">Tệp đính kèm</small>
                  <FileUploadManager
                    v-if="request.id"
                    :entity-id="request.id"
                    :entity="UPLOAD_ENTITY.BUDGET_LINE_REQUEST"
                    :disabled="true"
                  />
                </b-col>
              </b-row>

              <!-- Approval summary -->
              <b-row class="mb-4">
                <b-col cols="12" md="6">
                  <PurchaseRequestApprovalSummary :approvers="request.approvers || []" />
                </b-col>
              </b-row>
            </div>

            <div
              class="blr-detail-layout__resizer"
              :class="{ 'is-active': isSplitResizing }"
              role="separator"
              aria-orientation="vertical"
              aria-label="Điều chỉnh độ rộng cột"
              @mousedown.prevent="startSplitResize"
            />

            <div class="blr-detail-layout__aside">
              <CommentSection
                v-if="request.id"
                :module="COMMENT_MODULE.BUDGET_LINE_REQUEST"
                :reference-id="request.id"
                embedded
              />
            </div>
          </div>

          <!-- Modal submit -->
          <b-modal
            ref="submitModal"
            title="Gửi duyệt yêu cầu"
            @ok="confirmSubmit"
            ok-variant="success"
            ok-title="Gửi duyệt"
            cancel-title="Đóng"
          >
            <p>Bạn có chắc muốn gửi duyệt yêu cầu này để chờ duyệt?</p>
          </b-modal>

          <!-- Modal duyệt -->
          <b-modal
            ref="approveModal"
            title="Duyệt yêu cầu bổ sung"
            @ok="confirmApprove"
            ok-variant="success"
            ok-title="Duyệt"
            cancel-title="Đóng"
          >
            <b-form-group label="Ghi chú (nếu có)">
              <b-form-textarea
                v-model="approveForm.note"
                rows="3"
                placeholder="Nhập ghi chú"
              />
            </b-form-group>
          </b-modal>

          <!-- Modal không duyệt -->
          <b-modal
            ref="rejectModal"
            title="Không duyệt yêu cầu bổ sung"
            @ok="confirmReject"
            ok-variant="danger"
            ok-title="Không duyệt"
            cancel-title="Đóng"
          >
            <b-form-group>
              <template #label>
                Lý do <span class="text-danger">*</span>
              </template>
              <b-form-textarea
                v-model="rejectForm.note"
                rows="3"
                placeholder="Nhập lý do không duyệt"
                required
              />
            </b-form-group>
          </b-modal>

          <PurchaseRequestHistoryModal
            ref="historyModal"
            :histories="histories"
          />

          <!-- Modal hoàn duyệt -->
          <b-modal
            ref="undoModal"
            title="Hoàn duyệt yêu cầu bổ sung"
            @ok="confirmUndo"
            ok-variant="warning"
            ok-title="Hoàn duyệt"
            cancel-title="Đóng"
          >
            <p>Bạn có chắc muốn hoàn lại bước duyệt này?</p>
            <b-form-group label="Ghi chú (nếu có)">
              <b-form-textarea
                v-model="undoForm.note"
                rows="3"
                placeholder="Nhập ghi chú"
              />
            </b-form-group>
          </b-modal>
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { makeToastSuccess, makeToastFaile, convertPrice, handleError } from '@/utils/common';
import PurchaseRequestApprovalSummary from '../purchase-requests/components/PurchaseRequestApprovalSummary.vue';
import PurchaseRequestHistoryModal from '../purchase-requests/components/PurchaseRequestHistoryModal.vue';
import CommentSection from '@/view/components/comment/CommentSection.vue';
import FileUploadManager from '@/view/modules/upload/FileUploadManager.vue';
import moment from 'moment';
import {
  BUDGET_LINE_REQUEST_STATUS,
  BUDGET_LINE_REQUEST_STATUS_BADGE_VARIANT,
  WORKFLOW_APPROVAL_STEP_STATUS,
  COMMENT_MODULE,
  UPLOAD_ENTITY,
} from '@/utils/enum';
import { BUDGET_LINE_REQUEST_STATUS_NAME, HISTORY_ACTION_TYPE_CODE } from '@/utils/enum-name';

export default {
  components: { KTCodePreview, PurchaseRequestApprovalSummary, PurchaseRequestHistoryModal, CommentSection, FileUploadManager },
  beforeRouteEnter(_to, from, next) {
    next((vm) => { vm.returnQuery = from && from.name === 'list-budget-line-requests' ? { ...from.query } : {}; });
  },
  data() {
    return {
      BUDGET_LINE_REQUEST_STATUS,
      COMMENT_MODULE,
      UPLOAD_ENTITY,
      returnQuery: {},
      request: {},
      histories: [],
      approveForm: { note: '' },
      rejectForm: { note: '' },
      undoForm: { note: '' },
      splitLeftPercent: 66.666,
      isSplitResizing: false,
    };
  },
  computed: {
    splitMainWidth() {
      return `${this.splitLeftPercent}%`;
    },
  },
  created() {
    const id = this.$route.query.id;
    if (id) {
      this.fetchDetail(id);
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán', route: '#' },
      { title: 'Yêu cầu bổ sung NS', route: '/budget/budget-line-requests' },
      { title: 'Chi tiết' },
    ]);
    document.addEventListener('mousemove', this.onSplitResize);
    document.addEventListener('mouseup', this.stopSplitResize);
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.onSplitResize);
    document.removeEventListener('mouseup', this.stopSplitResize);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  },
  methods: {
    makeToastSuccess,
    makeToastFaile,
    convertPrice,
    formatPeriod(val) {
      return val ? moment(val).format('MM/YYYY') : '';
    },
    formatDateTime(val) {
      return val ? moment(val).format('DD/MM/YYYY HH:mm') : '';
    },
    statusVariant(val) {
      return BUDGET_LINE_REQUEST_STATUS_BADGE_VARIANT[val] || 'secondary';
    },
    statusName(val) {
      return BUDGET_LINE_REQUEST_STATUS_NAME[val] || '';
    },
    historyActionLabel(action) {
      return HISTORY_ACTION_TYPE_CODE[action] || String(action || '');
    },
    fetchDetail(id) {
      ApiService.get(`v2/budget-line-requests/${id}`)
        .then(({ data }) => {
          const result = data.data || {};
          this.request = result;
          this.histories = (result.history || []).map((h) => ({
            ...h,
            actionLabel: this.historyActionLabel(h.action),
          }));
        })
        .catch((err) => handleError(err));
    },
    showHistoryModal() {
      this.$refs.historyModal?.open();
    },
    editRequest() {
      const id = this.$route.query.id;
      this.$router.push({
        name: 'upsert-budget-line-request',
        query: { id, from: this.$route.fullPath },
      });
    },
    showSubmitModal() {
      this.$refs.submitModal.show();
    },
    confirmSubmit(evt) {
      if (evt && evt.preventDefault) evt.preventDefault();
      const id = this.$route.query.id;
      ApiService.post(`v2/budget-line-requests/${id}/submit`, {})
        .then(({ data }) => {
          this.makeToastSuccess(data.message || 'Submit thành công');
          this.$refs.submitModal.hide();
          this.fetchDetail(id);
        })
        .catch((err) => handleError(err));
    },
    showApproveModal() {
      this.approveForm.note = '';
      this.$refs.approveModal.show();
    },
    confirmApprove(evt) {
      if (evt && evt.preventDefault) evt.preventDefault();
      const id = this.$route.query.id;
      ApiService.patch(`v2/budget-line-requests/${id}/change-status`, {
        status: WORKFLOW_APPROVAL_STEP_STATUS.APPROVED,
        note: this.approveForm.note || '',
      })
        .then(({ data }) => {
          this.makeToastSuccess(data.message || 'Đã duyệt thành công');
          this.$refs.approveModal.hide();
          this.fetchDetail(id);
        })
        .catch((err) => handleError(err));
    },
    showRejectModal() {
      this.rejectForm.note = '';
      this.$refs.rejectModal.show();
    },
    confirmReject(evt) {
      if (evt && evt.preventDefault) evt.preventDefault();
      if (!this.rejectForm.note || !this.rejectForm.note.trim()) {
        this.makeToastFaile('Vui lòng nhập lý do không duyệt!');
        return;
      }
      const id = this.$route.query.id;
      ApiService.patch(`v2/budget-line-requests/${id}/change-status`, {
        status: WORKFLOW_APPROVAL_STEP_STATUS.REJECTED,
        note: this.rejectForm.note,
      })
        .then(({ data }) => {
          this.makeToastSuccess(data.message || 'Đã từ chối thành công');
          this.$refs.rejectModal.hide();
          this.fetchDetail(id);
        })
        .catch((err) => handleError(err));
    },
    showUndoModal() {
      this.undoForm.note = '';
      this.$refs.undoModal.show();
    },
    confirmUndo(evt) {
      if (evt && evt.preventDefault) evt.preventDefault();
      const id = this.$route.query.id;
      ApiService.patch(`v2/budget-line-requests/${id}/change-status`, {
        status: WORKFLOW_APPROVAL_STEP_STATUS.REFUND_APPROVED,
        note: this.undoForm.note || '',
      })
        .then(({ data }) => {
          this.makeToastSuccess(data.message || 'Hoàn duyệt thành công');
          this.$refs.undoModal.hide();
          this.fetchDetail(id);
        })
        .catch((err) => handleError(err));
    },
    confirmDelete() {
      this.$bvModal
        .msgBoxConfirm('Bạn có chắc muốn xóa yêu cầu này?', {
          title: 'Xác nhận xóa',
          okVariant: 'danger',
          okTitle: 'Xóa',
          cancelTitle: 'Đóng',
        })
        .then((confirmed) => {
          if (!confirmed) return;
          const id = this.$route.query.id;
          ApiService.delete(`v2/budget-line-requests/${id}`)
            .then(({ data }) => {
              this.makeToastSuccess(data.message || 'Đã xóa thành công');
              this.$router.push({ name: 'list-budget-line-requests', query: { ...this.returnQuery } });
            })
            .catch((err) => handleError(err));
        });
    },
    goBack() {
      this.$router.push({ name: 'list-budget-line-requests', query: { ...this.returnQuery } }).catch(() => {});
    },
    startSplitResize() {
      this.isSplitResizing = true;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },
    onSplitResize(event) {
      if (!this.isSplitResizing || !this.$refs.splitLayout) return;
      const rect = this.$refs.splitLayout.getBoundingClientRect();
      if (!rect.width) return;
      const nextPercent = ((event.clientX - rect.left) / rect.width) * 100;
      this.splitLeftPercent = Math.min(80, Math.max(45, nextPercent));
    },
    stopSplitResize() {
      if (!this.isSplitResizing) return;
      this.isSplitResizing = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    },
  },
};
</script>

<style scoped>
.blr-detail-layout {
  display: flex;
  align-items: stretch;
  gap: 0;
  min-height: 520px;
}

.blr-detail-layout__main {
  flex: 0 0 auto;
  min-width: 0;
  padding-right: 12px;
}

.blr-detail-layout__resizer {
  flex: 0 0 10px;
  margin: 0 2px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.08);
  cursor: col-resize;
  transition: background 0.15s ease;
  overflow: hidden;
}

.blr-detail-layout__resizer:hover,
.blr-detail-layout__resizer.is-active {
  background: rgba(54, 153, 255, 0.35);
}

.blr-detail-layout__aside {
  flex: 1 1 auto;
  min-width: 280px;
  min-height: 0;
  padding-left: 12px;
  border-left: 1px solid rgba(0, 0, 0, 0.08);
  max-height: calc(100vh - 180px);
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.blr-detail-layout__aside::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}
</style>
